import React, { useState, useEffect, useRef } from "react";
import { Sparkles, Mic, Target, Zap, ShieldCheck } from "lucide-react";
import "./HeroInteractiveCards.css";

export const HeroInteractiveCards = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeCard, setActiveCard] = useState(1); // Default center

  const cards = [
    {
      id: 0,
      tag: "Recurso Monyxx",
      title: "Voz",
      pill: "Velocidade",
      img: "/monyxx_robot_voice_v2.png",
      bottomTitle: "Registro Imediato",
      bottomDesc: "Entende áudio natural",
      icon: <Mic className="h-5 w-5" />
    },
    {
      id: 1,
      tag: "Recurso Monyxx",
      title: "Copiloto",
      pill: "Inteligência",
      img: "/monyxx_robot_copilot_v2.png",
      bottomTitle: "Análise Ativa",
      bottomDesc: "Categoriza e cruza dados",
      icon: <Zap className="h-5 w-5" />
    },
    {
      id: 2,
      tag: "Recurso Monyxx",
      title: "Futuro",
      pill: "Segurança",
      img: "/monyxx_robot_future_v5.png",
      bottomTitle: "Metas Protegidas",
      bottomDesc: "Projeções automáticas",
      icon: <ShieldCheck className="h-5 w-5" />
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      
      const scrollableDistance = rect.height - window.innerHeight;
      if (scrollableDistance <= 0) return;

      let progress = -rect.top / scrollableDistance;
      progress = Math.max(0, Math.min(1, progress));
      
      setScrollProgress(progress);
      
      // If scroll is near end, allow user to hover other cards.
      // Otherwise, heavily enforce center card as active to keep the highlight
      if (progress < 0.95) {
        setActiveCard(1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initialize
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className="hero-cards-section" data-testid="hero-interactive-cards">
      <div className="hero-sticky-container">
        <div className="hero-glow-1"></div>
        <div className="hero-glow-2"></div>
        
        <div 
          className="hero-title-wrapper reveal"
          style={{ 
            opacity: 1 - scrollProgress * 2.5, 
            transform: `translateY(${-scrollProgress * 50}px)`,
            pointerEvents: scrollProgress > 0.4 ? 'none' : 'auto'
          }}
        >
          <span className="pill mb-4 mx-auto" style={{display: 'inline-flex'}}>
            <Sparkles className="h-3.5 w-3.5" /> Nova era da gestão financeira pessoal
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight text-white pb-2">
            Quem controla o dinheiro<br />
            controla o <span className="monyx-grad-text inline-block pb-1">futuro.</span><br />
            O Monyxx é seu <span className="monyx-grad-text inline-block pb-1">controle.</span>
          </h1>
        </div>

        <div className="hero-cards-container">
          {cards.map((card) => {
            const isActive = activeCard === card.id;
            
            let xOffset = 0;
            // Opacity and scale base are handled by CSS classes (.active vs non-active)
            // But for the entrance animation we override inline
            let baseOpacity = isActive ? 1 : 0.6; // target opacities when fully visible
            
            // INVERTED LOGIC: Starts invisible/far, slides IN slowly as you scroll
            // progress * 1.5 faz a animação levar mais tempo (parecendo câmera lenta)
            const slideInProgress = Math.min(1, scrollProgress * 1.5); 
            const invProgress = 1 - slideInProgress;
            
            // Override inline opacity during the fly-in animation
            let currentOpacity = baseOpacity;

            if (card.id === 0) {
              xOffset = -invProgress * 200; // Move only 200px outwards instead of off-screen
              currentOpacity = Math.max(0.2, slideInProgress) * baseOpacity; // Never fully disappear
            } else if (card.id === 2) {
              xOffset = invProgress * 200; // Move only 200px outwards instead of off-screen
              currentOpacity = Math.max(0.2, slideInProgress) * baseOpacity; // Never fully disappear
            } else if (card.id === 1) {
              // Center card can have a slight vertical drop or just stay pinned
              // It starts a bit larger (e.g., 1.1) and shrinks back down to its .active scale
              // We'll let CSS handle the scale.
              currentOpacity = 1;
            }

            // Distância final das laterais (quanto eles ficam afastados do centro)
            let defaultX = 0;
            if (card.id === 0) defaultX = -340; // Um pouco mais longe do centro
            if (card.id === 1) defaultX = 0;
            if (card.id === 2) defaultX = 340;

            const finalTransform = `translateX(calc(${defaultX}px + ${xOffset}px))`;

            return (
              <div 
                key={card.id}
                className={`hero-card ${isActive ? 'active' : ''}`}
                onMouseEnter={() => {
                  if (scrollProgress >= 0.95) setActiveCard(card.id)
                }}
                style={{
                  transform: finalTransform,
                  opacity: currentOpacity,
                  // Efeito de "Câmera Lenta" com transition enquanto rola (scrollProgress < 0.99)
                  // Quando para de rolar (>= 0.99), ativa as transições de hover normais
                  transition: scrollProgress >= 0.99 
                    ? 'transform 0.4s ease, filter 0.4s ease, opacity 0.4s ease, width 0.4s ease, height 0.4s ease' 
                    : 'transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.8s ease'
                }}
              >
                <img src={card.img} alt={card.title} className="hero-card-img" loading="eager" />
                <div className="hero-card-content">
                  <div className="hero-card-header">
                    <span className="hero-card-tag">{card.tag}</span>
                    <div className="hero-card-pill">{card.pill}</div>
                  </div>
                  
                  <div className="hero-card-footer">
                    <h3 className="hero-card-title">{card.title}</h3>
                    <p className="hero-card-desc">{card.bottomDesc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
